/**
 * Time tracking contracts — DESIGN.md §6.13 (R30, D14).
 *
 * Two measures the design insists on keeping distinct: *elapsed* time (calendar
 * time in statuses, derived from the audit stream) and *effort* (person-time
 * logged against cards). These schemas describe the second.
 *
 * Durations cross the wire as whole minutes. The `1h 30m` shorthand is a
 * presentation concern, parsed and formatted by `domain/duration.ts` on both
 * sides so the API never has to guess what "1.5" meant.
 */
import { z } from 'zod';
import { TIME_SOURCES } from '../enums.js';
import { dateSchema, optionalText, paginationSchema, uuidSchema } from './common.js';
import { userRefSchema } from './users.js';
/** A day's worth of effort, per user, per card. */
export const timeEntrySchema = z.object({
    id: uuidSchema,
    taskId: uuidSchema,
    user: userRefSchema,
    minutes: z.number().int().positive(),
    spentOn: z.string(),
    note: z.string().nullable(),
    source: z.enum(TIME_SOURCES),
    startedAt: z.string().nullable(),
    endedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** Whether the viewer may edit this entry — own, or any as admin (§6.13). */
    canEdit: z.boolean(),
});
/** Minutes only — the API never receives `1h 30m`, only what it parsed to. */
const minutesSchema = z
    .number()
    .int()
    .min(1, 'Log at least a minute')
    .max(24 * 60, 'A single entry cannot exceed 24 hours');
export const createTimeEntryRequestSchema = z
    .object({
    minutes: minutesSchema,
    /** Defaults to today in the caller's timezone (D10). */
    spentOn: dateSchema.optional(),
    note: optionalText(2000),
})
    .strict();
export const updateTimeEntryRequestSchema = z
    .object({
    minutes: minutesSchema.optional(),
    spentOn: dateSchema.optional(),
    note: optionalText(2000),
})
    .strict();
export const listTimeEntriesQuerySchema = paginationSchema;
/**
 * A running timer — §6.13, as amended by card #4.
 *
 * Server-side, so it survives a logout and follows the user from laptop to
 * phone. There may now be **several at once**: §6.13's "one running timer per
 * user" was reversed because botsitting several agents means several cards
 * genuinely are in progress at the same moment, and the old rule forced that
 * to be recorded as a lie. One timer per card, still — two on the same card
 * would double-count.
 */
export const activeTimerSchema = z.object({
    taskId: uuidSchema,
    taskNumber: z.number().int().positive(),
    taskTitle: z.string(),
    startedAt: z.string(),
    /** Server-computed, so a clock-skewed browser cannot invent elapsed time. */
    elapsedMinutes: z.number().int().nonnegative(),
    /** §6.13 — past 12 h, ask before logging a forgotten night. */
    needsConfirmation: z.boolean(),
});
/**
 * `stopRunning` is gone (#4). It existed to answer "a timer is already running
 * — stop and log it?", a question that no longer has to be asked: starting a
 * second timer is now simply allowed. Removing it rather than accepting and
 * ignoring it means a client still sending it gets a 400 and is fixed, instead
 * of silently believing it asked for something.
 */
export const startTimerRequestSchema = z.object({ taskId: uuidSchema }).strict();
/** Which timer to stop — required now that there may be more than one (#4). */
export const stopTimerRequestSchema = z
    .object({ taskId: uuidSchema, note: optionalText(2000) })
    .strict();
export const discardTimerRequestSchema = z.object({ taskId: uuidSchema }).strict();
/** What the card panel's Time row renders — §6.13. */
export const taskTimeSummarySchema = z.object({
    estimateMinutes: z.number().int().nullable(),
    loggedMinutes: z.number().int().nonnegative(),
    /** Display-level rollup; nothing is stored (§6.13). */
    loggedIncludingSubtasksMinutes: z.number().int().nonnegative(),
    byUser: z.array(z.object({ user: userRefSchema, minutes: z.number().int() })),
    entries: z.array(timeEntrySchema),
});
//# sourceMappingURL=time.js.map